import { useCallback, useEffect, useState } from 'react'
import { sendGet, sendPostFormData } from 'hooks/use-fetch'
import { type AddDocumentHandler, type DocumentRequirements } from 'Components/Documents'

interface ServiceDocuments {
  data: DocumentRequirements | null
  isLoading: boolean
  addDocument: AddDocumentHandler
}

/** Loads a service's required documents for the caregiver portal and saves new ones against it. */
export function useServiceDocuments (enrollmentID: number): ServiceDocuments {
  const [data, setData] = useState<DocumentRequirements | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async (): Promise<void> => {
    const { response, success } = await sendGet(`/CaregiverPortal/GetServiceDocuments/${enrollmentID}`, {})
    setData(success ? response : null)
    setIsLoading(false)
  }, [enrollmentID])

  useEffect(() => { void load() }, [load])

  const addDocument: AddDocumentHandler = async (pending) => {
    const form = new FormData()
    form.append('enrollmentID', enrollmentID.toString())
    form.append('documentTypeID', pending.documentTypeID.toString())
    form.append('includeChild', pending.includeChild.toString())
    pending.caregiverIDs.forEach(id => { form.append('caregiverIDs', id.toString()) })
    if (pending.incomeAmount !== null) form.append('incomeAmount', pending.incomeAmount.toString())
    if (pending.incomeYear !== null) form.append('incomeYear', pending.incomeYear.toString())
    form.append('file', pending.file)

    const { response, success, error } = await sendPostFormData('/CaregiverPortal/AddServiceDocument', form)
    if (!success) return error.length > 0 ? error : ['The document could not be saved.']
    const errors: string[] = (response?.errors ?? []).map((e: { error: string }) => e.error)
    if (errors.length === 0) await load()
    return errors
  }

  return { data, isLoading, addDocument }
}
